import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

export interface ToastConfig {
  id?: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title?: string;
  message?: string;
  duration?: number;
  dismissible?: boolean;
  progressBar?: boolean;
  data?: any;
}

@Injectable({
  providedIn: 'root'
})
export class ToastService {
  private toasts = new BehaviorSubject<ToastConfig[]>([]);
  toasts$ = this.toasts.asObservable();

  private defaultDuration = 5000; // 5 seconds
  private maxToasts = 1; // Maximum number of toasts to show at once

  show(config: ToastConfig): void {

    // Remove oldest toast if we exceed maxToasts
    const currentToasts = this.toasts.value;
    if (currentToasts.length >= this.maxToasts) {
      this.remove(currentToasts[0]?.id || '');
    }

    if (!config.id) {
      config.id = uuidv4();
    }

    if (config.duration === undefined) {
      config.duration = this.defaultDuration;
    }

    this.toasts.next([...this.toasts.value, config]);
  }

  showSuccess(message: string, title: string = 'Success', duration: number = this.defaultDuration): void {
    this.show({
      type: 'success',
      title,
      message,
      duration,
      progressBar: true
    });
  }

  showError(message: string, title: string = 'Error'): void {
    this.show({
      type: 'error',
      title,
      message,
      duration: 0, // Errors stay until manually dismissed
      dismissible: true,
      progressBar: false
    });
  }

  showWarning(message: string, title: string = 'Warning', duration: number = this.defaultDuration * 1.5): void {
    this.show({
      type: 'warning',
      title,
      message,
      duration,
      progressBar: true
    });
  }

  showInfo(message: string, title: string = 'Info', duration: number = this.defaultDuration): void {
    this.show({
      type: 'info',
      title,
      message,
      duration,
      progressBar: true
    });
  }

  remove(id: string): void {
    const currentToasts = this.toasts.value;
    this.toasts.next(currentToasts.filter(t => t.id !== id));
  }

  clear(): void {
    this.toasts.next([]);
  }

  setDefaultDuration(duration: number): void {
    this.defaultDuration = duration;
  }

  setMaxToasts(max: number): void {
    this.maxToasts = max;
  }
}
